//import React from 'react';
import './sentryInit.ts';
import { FC, Suspense, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { Provider } from 'react-redux';
import { RelayEnvironmentProvider } from 'react-relay';
import { PrimeReactProvider, type APIOptions } from 'primereact/api';
import 'primereact/resources/primereact.min.css'; //core css
import 'primeicons/primeicons.css'; //icons
import 'primeflex/primeflex.css'; // flex
import { store } from './redux/store.ts';
import './assets/nc_icons/style.css';
import './index.scss';
import '../node_modules/video-react/dist/video-react.css';
import { relayEnvironment } from './relay.ts';
import router from './router.tsx';
import { Auth0ToRedux } from './Auth0ToRedux.tsx';
import { MainFallback } from './components/Fallback/MainFallBack.tsx';
import PersistingQuerysWrapper from './PersistingQuerysWrapper.tsx';
import CustomErrorBoundary from './components/LayoutWrapper/CustomErrorBoundary.tsx';
import { ErrorBoundaryUI } from './components/ErrorBoundary/ErrorBoundaryUI.tsx';
import { UnleashProvider } from './UnleashProvider.tsx';
import { ExperimentsProvider } from './experiments/ExperimentsProvider.tsx';
import { BrainBreakProvider } from './contexts/brainBreakContext.tsx';
import { TimeOnPlatformTracker } from './components/TimeOnPlatformTracker/TimeOnPlatformTracker.tsx';
import { WeeklyGoalProvider } from './contexts/WeeklyGoalContext.tsx';
import { RetakeCountdownProvider } from './contexts/RetakeCountdownContext.tsx';
import posthog from 'posthog-js';
import { PostHogProvider } from '@posthog/react';
import { disableEventSending, enableEventSending, initializePostHog } from './utils/posthog.ts';
import { setWasmUrl } from '@lottiefiles/dotlottie-react';
import dotLottieWasmUrl from '@lottiefiles/dotlottie-web/dotlottie-player.wasm?url';
import { useAppSelector } from './redux/hooks.ts';
import { selectUser } from './redux/reducers/userSlice.ts';

setWasmUrl(dotLottieWasmUrl);

// Drops PrimeReact's invalid `role="separator"` on breadcrumb separators (WCAG 4.1.2) and hides them from screen readers.
const primeReactConfig: Partial<APIOptions> = {
  pt: {
    breadcrumb: {
      separator: { role: undefined, 'aria-hidden': 'true' },
    },
  },
};

const RelayWrapper: FC = () => {
  const { getAccessTokenSilently, logout } = useAuth0();
  const userRedux = useAppSelector(selectUser);
  useEffect(() => {
    const excludeInternalUsers: boolean = import.meta.env.VITE_POSTHOG_EXCLUDE_INTERNAL_USERS != 'false';
    if (userRedux?.isInternalUser && excludeInternalUsers) {
      disableEventSending(posthog);
    } else {
      enableEventSending(posthog);
    }
  }, [userRedux?.isInternalUser]);

  // Build the environment once. Calling relayEnvironment() inline rebuilt it — with a brand-new
  // empty Store — on every Auth0 state change: a real token refresh mints a new id_token, so
  // auth0-react hands back a new `user` object (its reducer only bails on reference equality) and
  // re-renders this component. That wiped the Relay cache and refetched every mounted query on
  // every refresh, which in turn replayed server-derived UI state mid-assessment. Both callbacks
  // are useCallback([client]) and `posthog` is a module singleton, so this never re-runs in
  // practice. No token is captured here: fetchGraphQL reads it from Redux per request.
  const environment = useMemo(
    () => relayEnvironment(getAccessTokenSilently, logout, posthog),
    [getAccessTokenSilently, logout],
  );

  return (
    <PostHogProvider client={posthog}>
      <RelayEnvironmentProvider environment={environment}>
        <CustomErrorBoundary fallback={<ErrorBoundaryUI />}>
          <Suspense fallback={<MainFallback />}>
            <Auth0ToRedux>
              <PersistingQuerysWrapper>
                <UnleashProvider>
                  <ExperimentsProvider>
                    <BrainBreakProvider>
                      <WeeklyGoalProvider>
                        <RetakeCountdownProvider>
                          <TimeOnPlatformTracker>
                            <RouterProvider router={router} />
                          </TimeOnPlatformTracker>
                        </RetakeCountdownProvider>
                      </WeeklyGoalProvider>
                    </BrainBreakProvider>
                  </ExperimentsProvider>
                </UnleashProvider>
              </PersistingQuerysWrapper>
            </Auth0ToRedux>
          </Suspense>
        </CustomErrorBoundary>
      </RelayEnvironmentProvider>
    </PostHogProvider>
  );
};

const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'localhost/not set'; // A bit cheeky but should be right
const release = import.meta.env.VITE_RELEASE_VERSION ?? '';
initializePostHog(posthog, { environment, release });

ReactDOM.createRoot(document.getElementById('root')!).render(
  //<React.StrictMode>

  <Auth0Provider
    domain={import.meta.env.VITE_DOMAIN || ''}
    clientId={import.meta.env.VITE_CLIENT_ID || ''}
    authorizationParams={{
      redirect_uri: window.location.origin,
      scope: 'openid profile email offline_access',
    }}
    useRefreshTokens={true}
    cacheLocation="localstorage"
    useRefreshTokensFallback={false}
  >
    <Provider store={store}>
      <PrimeReactProvider value={primeReactConfig}>
        <RelayWrapper />
      </PrimeReactProvider>
    </Provider>
  </Auth0Provider>,
  //</React.StrictMode>,
);
